import { UserPermissionService } from 'src/app/shared/services/user-permission.service';
import { OnInit, OnDestroy } from '@angular/core';
import { Component } from '@angular/core';
import { combineLatest, map, Observable, fromEvent, Subscription } from 'rxjs';
import { startWith, map as rxjsMap } from 'rxjs/operators';
import { TransactionsComponent } from '../features/transactions/transactions.component';
import { LayoutService } from './service/app.layout.service';
import { Store } from '@ngrx/store';
import { selectSubscription, selectTrialDaysRemaining } from 'src/app/shared/state/subscription/subscription.selectors';
import { SubscriptionStatusResponse } from 'src/app/shared/interfaces/stripe/stripe-interface';
import { ToastServiceService } from 'src/app/shared/services/toast-service.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  styleUrls: ['./app.menu.component.scss'],
})
export class AppMenuComponent implements OnInit, OnDestroy {
  menuItems$: Observable<any[]> | null = null; // ✅ Store menu as an Observable
  trialDaysRemaining: number | null = null;
  subscriptionStatus: SubscriptionStatusResponse | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private userPermissionService: UserPermissionService,
    public layoutService: LayoutService,
    private store: Store,
    private toastService: ToastServiceService,
    private router: Router
  ) {}

  ngOnInit() {
    this.menuItems$ = this.getMenuItems(); // ✅ Assign Observable

    // Subscribe to subscription status from store (for mobile/tablet)
    const subscriptionSub = this.store.select(selectSubscription).subscribe((status: SubscriptionStatusResponse | null) => {
      this.subscriptionStatus = status;
    });

    // Subscribe to trial days remaining from store
    const trialDaysSub = this.store.select(selectTrialDaysRemaining).subscribe((days: number | null) => {
      this.trialDaysRemaining = days;
    });

    this.subscriptions.push(subscriptionSub, trialDaysSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onUpgradeButtonClick() {
    // Navigate to subscription pricing page with return URL
    const currentUrl = this.router.url;
    this.router.navigate(['/subscription'], { queryParams: { returnUrl: currentUrl } });
  }

  getMenuItems(): Observable<any[]> {
    // Create observable for mobile check
    const isMobile$ = fromEvent(window, 'resize').pipe(
      startWith(null),
      rxjsMap(() => this.layoutService.isMobile())
    );

    return combineLatest([
      this.userPermissionService.companyAdmininstrator(),
      this.userPermissionService.employeeManagement(),
      this.userPermissionService.inventoryManagement(),
      this.userPermissionService.transactionsManagement(),
      isMobile$,
    ]).pipe(
      map(([companyAdmin, employeeManagement, inventoryManagement, transactionsManagement, isMobile]) => [
        {
          label: 'menu',
          icon: 'pi pi-th-large',
          headerVisisble: false,
          visible: true,
          items: [
            {
              label: 'Αρχική',
              icon: 'pi pi-fw pi-home',
              routerLink: ['/'],
              visible: true,
            },
            {
              label: 'Διαχείριση Βάρδιας',
              icon: 'pi pi-fw pi-history',
              routerLink: ['/shift-management'],
              visible: isMobile, // show only in mobile
            },
            {
              label: 'Εταιρεία',
              icon: 'pi pi-fw pi-building',
              routerLink: ['/company'],
              visible: companyAdmin,
            },
            {
              label: 'Αποθήκη',
              icon: 'pi pi-fw pi-tags',
              routerLink: ['/inventory-management'],
              visible: inventoryManagement,
            },
            {
              label: 'Ενημερώσεις Αποθήκης',
              icon: 'pi pi-fw pi-sync',
              routerLink: ['/inventory-updates'],
              visible: inventoryManagement,
            },
            {
              label: 'Εργαζόμενοι',
              icon: 'pi pi-fw pi-users',
              routerLink: ['/employee'],
              visible: employeeManagement,
            },
            {
              label: 'Πρόγραμμα εργαζομένων',
              icon: 'pi pi-fw pi-clock',
              routerLink: ['/employee-schedule'],
              visible: true,
            },
            {
              label: 'Παρουσιολόγιο',
              icon: 'pi pi-fw pi-calendar',
              routerLink: ['/notfound'],
              visible: false,
            },
            {
              label: 'Έσοδα/Έξδοδα',
              icon: 'pi pi-fw pi-credit-card',
              routerLink: ['/notfound'],
              visible: false,
            },
            {
              label: 'Συναλλαγές',
              icon: 'pi pi-fw pi-arrow-right-arrow-left',
              routerLink: ['/transactions'],
              visible: transactionsManagement,
            },
          ],
        },
      ])
    );
  }
}
